/**
 * Results Page JavaScript
 * Displays analysis results and handles product recommendations and cart integration
 */

class ResultsPage {
  constructor() {
    this.analysisData = null;
    this.recommendationEngine = null;
    this.recommendedProducts = [];
    this.routine = null;
    
    this.init();
  }

  async init() {
    // Show loading state
    this.showLoading();

    // Load analysis data
    this.loadAnalysisData();

    // Initialize recommendation engine
    if (typeof RecommendationEngine !== 'undefined') {
      this.recommendationEngine = new RecommendationEngine();
    } else {
      console.error('RecommendationEngine not found');
      this.hideLoading();
      return;
    }

    // Perform analysis
    await this.performAnalysis();

    // Setup cart integration (buttons)
    this.setupCartIntegration();

    // Hide loading state
    this.hideLoading();

    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  renderResults() {
    // This method is called after analysis is complete
    // All rendering is handled in renderAnalysisSummary and renderRoutine
    console.log('Results rendered successfully');
  }

  showLoading() {
    const routineContainer = document.getElementById('routine-steps');
    if (routineContainer) {
      routineContainer.innerHTML = `
        <div style="text-align: center; padding: 3rem;">
          <div class="loading-spinner" style="margin: 0 auto 1rem;"></div>
          <p style="color: var(--text-medium); font-size: 1.1rem;">
            Analyzing your skin and creating your personalized routine...
          </p>
        </div>
      `;
    }
  }

  hideLoading() {
    // Loading is automatically hidden when content is rendered
  }

  loadAnalysisData() {
    // Try to get from localStorage
    const stored = localStorage.getItem('skinAnalysisData');
    if (stored) {
      try {
        this.analysisData = JSON.parse(stored);
        return;
      } catch (e) {
        console.error('Failed to parse stored data:', e);
      }
    }

    // Try to get from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get('data');
    if (dataParam) {
      try {
        this.analysisData = JSON.parse(decodeURIComponent(dataParam));
        return;
      } catch (e) {
        console.error('Failed to parse URL data:', e);
      }
    }

    // If no data found, redirect to skin test
    alert('No analysis data found. Please complete the skin test first.');
    window.location.href = '/pages/skin-test';
  }

  async performAnalysis() {
    if (!this.analysisData || !this.recommendationEngine) {
      return;
    }

    try {
      const result = await this.recommendationEngine.analyze(
        this.analysisData.questionnaire,
        this.analysisData.imageResults
      );

      this.analysisResult = result;
      this.recommendedProducts = result.recommendedProducts || [];
      this.routine = result.routine || {};

      // Store result for rendering
      this.renderAnalysisSummary(result);
      this.renderRoutine(result);

    } catch (error) {
      console.error('Analysis failed:', error);
      alert('Failed to generate recommendations. Please try again.');
    }
  }

  renderAnalysisSummary(result) {
    // Update profile photos (both old and new circular gauge)
    const profilePhoto = document.getElementById('profile-photo');
    const profilePhotoGauge = document.getElementById('profile-photo-gauge');
    if (this.analysisData.uploadedImage) {
      if (profilePhoto) profilePhoto.src = this.analysisData.uploadedImage;
      if (profilePhotoGauge) profilePhotoGauge.src = this.analysisData.uploadedImage;
    }

    // Update skin age displays
    const calculatedAge = this.calculateSkinAge(result);
    const skinAgeDisplay = document.getElementById('skin-age-display');
    const skinAgeDisplayGauge = document.getElementById('skin-age-display-gauge');
    if (skinAgeDisplay) skinAgeDisplay.textContent = calculatedAge;
    if (skinAgeDisplayGauge) skinAgeDisplayGauge.textContent = calculatedAge;

    // Update circular gauge and overall score
    this.updateCircularGauge(result);
    this.updateOverallScore(result);
    this.updatePrimaryConcern(result);
    this.updateSocialProof(result);

    // Update profile card concerns - Use AI-detected concerns, not questionnaire
    const profilePrimary = document.getElementById('profile-primary-concern');
    const profileSecondary = document.getElementById('profile-secondary-concern');
    
    if (profilePrimary && result.primaryConcern) {
      profilePrimary.textContent = this.formatConcern(result.primaryConcern);
      profilePrimary.style.display = 'inline-block';
    } else if (profilePrimary) {
      profilePrimary.style.display = 'none';
    }
    
    if (profileSecondary && result.secondaryConcern) {
      profileSecondary.textContent = this.formatConcern(result.secondaryConcern);
      profileSecondary.style.display = 'inline-block';
    } else if (profileSecondary) {
      profileSecondary.style.display = 'none';
    }

    // Update score boxes with REAL calculated values
    this.updateScoreBoxes(result);

    // Render ALL concerns (not just 2)
    this.renderAllConcerns(result);
    
    // Render primary concerns badges (AI + user)
    this.renderPrimaryConcernsBadges(result);

    // Update before/after photo
    if (this.analysisData.uploadedImage) {
      const beforePhoto = document.getElementById('before-photo');
      const beforeAfterSection = document.getElementById('before-after-section');
      if (beforePhoto) {
        beforePhoto.src = this.analysisData.uploadedImage;
      }
      if (beforeAfterSection) {
        beforeAfterSection.style.display = 'block';
      }
    }

    // Render priority-based products if available
    if (result.priorities && result.productsByPriority) {
      this.renderPriorityProducts(result.priorities, result.productsByPriority);
    }
  }

  calculateSkinAge(result) {
    const actualAge = parseInt(this.analysisData.questionnaire.age) || 25;
    
    // Use Gemini's skin age estimate if available
    if (result.metrics && result.metrics.skin_age_estimate) {
      return result.metrics.skin_age_estimate;
    }
    
    // Fallback: calculate based on scores (0-100 where higher = better)
    const concerns = result.concerns || {};
    const avgScore = Object.keys(concerns).length > 0
      ? Object.values(concerns).reduce((sum, val) => sum + val, 0) / Object.keys(concerns).length
      : 85;
    
    // Lower average score = worse skin = older looking
    // 100 avg = -5 years, 50 avg = +5 years
    const ageModifier = Math.round((80 - avgScore) / 10);
    
    return Math.max(18, Math.min(60, actualAge + ageModifier));
  }

  renderAllConcerns(result) {
    const concernsContainer = document.querySelector('.concerns-badges');
    if (!concernsContainer) return;

    concernsContainer.innerHTML = '';

    // Get all concerns from the questionnaire (field name is "concerns")
    let allConcerns = this.analysisData.questionnaire.concerns || [];
    
    console.log('Questionnaire concerns:', allConcerns);
    console.log('Analysis result:', result);
    
    // Get concerns scores from the analysis result
    const concernScores = result.concerns || result.scores || {};
    console.log('Concern scores:', concernScores);
    
    // Also include concerns detected by ML if available
    if (concernScores && typeof concernScores === 'object') {
      Object.keys(concernScores).forEach(concern => {
        const formattedConcern = this.formatConcern(concern);
        if (!allConcerns.some(c => this.formatConcern(c) === formattedConcern)) {
          allConcerns.push(concern);
        }
      });
    }

    // If still no concerns, show a message
    if (allConcerns.length === 0) {
      concernsContainer.innerHTML = '<span class="concern-badge secondary">No specific concerns</span>';
      return;
    }

    // Render all concerns
    allConcerns.forEach((concern, index) => {
      const concernKey = concern.toLowerCase().replace(/\s+/g, '-');
      const severity = concernScores[concernKey] || 3;
      const badge = document.createElement('span');
      badge.className = `concern-badge ${index === 0 ? 'primary' : 'secondary'}`;
      badge.textContent = this.formatConcern(concern);
      badge.title = `Severity: ${severity}/5`;
      concernsContainer.appendChild(badge);
    });
  }

  updateScoreBoxes(result) {
    const scoreBoxesContainer = document.getElementById('score-boxes');
    if (!scoreBoxesContainer) return;

    scoreBoxesContainer.innerHTML = '';

    const analysisResult = result.concerns || result.scores || {};
    const questionnaire = this.analysisData.questionnaire;
    const userConcerns = questionnaire.concerns || [];

    // ALL 6 concern types - always show all
    const allConcernTypes = [
      'acne',
      'pigmentation',
      'anti-aging',
      'pore',
      'hydration',
      'barrier-repair'
    ];

    console.log('Rendering ALL concern scores');
    console.log('User selected concerns:', userConcerns);
    console.log('Analysis scores:', analysisResult);

    // Create score box for EACH of the 6 concerns
    allConcernTypes.forEach(concernKey => {
      // Check if user selected this concern
      const isUserConcern = userConcerns.some(c => 
        c.toLowerCase().replace(/\s+/g, '-') === concernKey
      );
      
      // Get score from Gemini (0-100 where higher = better)
      const score = analysisResult[concernKey] || 50; // Default to 50 if not available
      
      const scoreBox = document.createElement('div');
      scoreBox.className = `score-box`;
      scoreBox.innerHTML = `
        <div class="score-percentage">${Math.round(score)}%</div>
        <div class="score-bar">
          <div class="score-bar-fill" style="width: ${score}%"></div>
        </div>
        <div class="score-label">
          ${this.formatConcern(concernKey)}
        </div>
      `;
      
      scoreBoxesContainer.appendChild(scoreBox);
    });

    // Add overall score at the end
    const overallScore = this.calculateOverallScore(analysisResult, questionnaire);
    const overallBox = document.createElement('div');
    overallBox.className = 'score-box overall-score';
    overallBox.innerHTML = `
      <div class="score-percentage">${Math.round(overallScore)}%</div>
      <div class="score-bar">
        <div class="score-bar-fill" style="width: ${overallScore}%"></div>
      </div>
      <div class="score-label">Overall Score</div>
    `;
    scoreBoxesContainer.appendChild(overallBox);

    // Animate the bars after a short delay
    setTimeout(() => {
      const fills = scoreBoxesContainer.querySelectorAll('.score-bar-fill');
      fills.forEach(fill => {
        fill.style.transition = 'width 1s ease';
      });
    }, 100);
    
    // Add carousel dots for scrolling
    this.addScoreCarouselDots(7); // 6 concerns + 1 overall
  }

  calculateMetricScore(severity, questionnaire, concernType) {
    // Base score of 100
    let score = 100;
    
    // Reduce score based on severity (1-5)
    if (severity) {
      score -= severity * 12; // Each severity point reduces score by 12%
    }

    // Adjust based on skin type
    const skinType = questionnaire.skinType;
    
    // Type-specific adjustments
    if (concernType === 'pore' && (skinType === 'oily' || skinType === 'combination')) {
      score -= 8; // Oily skin has more visible pores
    }
    
    if (concernType === 'acne' && skinType === 'oily') {
      score -= 10; // Oily skin more prone to acne
    }
    
    if (concernType === 'hydration' && skinType === 'dry') {
      score -= 10; // Dry skin needs more hydration
    }
    
    if (concernType === 'anti-aging') {
      const age = parseInt(questionnaire.age);
      if (age > 35) score -= 10;
      if (age > 45) score -= 15;
    }

    // Ensure score is between 20-100
    return Math.max(Math.min(score, 100), 20);
  }

  calculateOverallScore(concerns, questionnaire) {
    // Use Gemini's overall_skin_quality if available
    if (this.analysisResult && this.analysisResult.metrics && this.analysisResult.metrics.overall_skin_quality) {
      return this.analysisResult.metrics.overall_skin_quality;
    }
    
    // Calculate average of all concern scores (0-100 where higher = better)
    const concernScores = Object.values(concerns).filter(v => typeof v === 'number');
    
    if (concernScores.length === 0) return 75; // Default
    
    const avgScore = concernScores.reduce((sum, val) => sum + val, 0) / concernScores.length;
    
    return Math.round(Math.max(20, Math.min(100, avgScore))); // Between 20-100%
  }

  renderPriorityProducts(priorities, productsByPriority) {
    const container = document.querySelector('.priority-products-container');
    if (!container) return;

    container.innerHTML = '';
    
    priorities.forEach((priority, index) => {
      const products = productsByPriority[priority.concern]?.products || [];
      if (products.length === 0) return;

      const prioritySection = document.createElement('div');
      prioritySection.className = 'priority-group';
      prioritySection.innerHTML = `
        <div class="priority-label">
          <span class="priority-badge ${priority.level}">${priority.level.toUpperCase()} PRIORITY</span>
          <h3 class="priority-title">${this.formatConcern(priority.concern)}</h3>
        </div>
        <div class="products-grid" id="products-${priority.concern}"></div>
      `;

      container.appendChild(prioritySection);

      // Render products
      const productsGrid = document.getElementById(`products-${priority.concern}`);
      products.forEach(product => {
        const productCard = this.createProductCard(product);
        productsGrid.appendChild(productCard);
      });
    });
  }

  updateScoreGauge(score) {
    const gaugeFill = document.getElementById('gauge-fill');
    if (gaugeFill) {
      const circumference = 2 * Math.PI * 90; // radius = 90
      const offset = circumference - (score / 100) * circumference;
      gaugeFill.style.strokeDasharray = circumference;
      gaugeFill.style.strokeDashoffset = offset;
    }
  }

  updateMetrics(metrics) {
    const metricNames = ['pores', 'pigmentation', 'wrinkles', 'texture'];
    
    metricNames.forEach(metric => {
      const value = metrics[metric] || 0;
      const fillEl = document.getElementById(`metric-${metric}-fill`);
      const valueEl = document.getElementById(`metric-${metric}-value`);
      
      if (fillEl) {
        fillEl.style.width = `${value}%`;
      }
      if (valueEl) {
        valueEl.textContent = `${Math.round(value)}%`;
      }
    });
  }

  async renderRoutine(result) {
    // Use JavaScript to render products from product handles array
    if (window.productSlider && result.productsByPriority) {
      console.log('Rendering products with slider:', result.productsByPriority);
      
      // Transform products to slider format
      const sliderData = {};
      
      // ✅ Only show products for PRIMARY concern (from product handles array)
      if (result.primaryConcern) {
        const concernKey = result.primaryConcern;
        const concernProducts = result.productsByPriority?.[concernKey]?.products || [];
        
        if (concernProducts.length > 0) {
          sliderData[concernKey] = concernProducts.map(product => {
            const variant = product.variants && product.variants[0];

            // Get image from multiple possible sources (Shopify JSON varies)
            let imageUrl = null;
            if (product.featured_image) {
              imageUrl = product.featured_image;
            } else if (product.images && product.images.length > 0) {
              imageUrl = typeof product.images[0] === 'string'
                ? product.images[0]
                : (product.images[0].src || product.images[0]);
            } else if (product.image) {
              imageUrl = product.image;
            }

            // Debug logging
            console.log(`Product: ${product.title}`, {
              featured_image: product.featured_image,
              images: product.images,
              image: product.image,
              final_imageUrl: imageUrl
            });

            return {
              id: product.id,
              variantId: variant ? variant.id : product.id,
              title: product.title,
              handle: product.handle,
              vendor: product.vendor,
              tags: product.tags,
              url: `/products/${product.handle}`,
              // pass through as product.image for slider
              image: imageUrl,
              price: variant ? variant.price / 100 : 0,
              compareAtPrice: variant && variant.compare_at_price ? variant.compare_at_price / 100 : null,
              available: variant ? variant.available : true
            };
          });
          
          console.log(`Showing ${concernProducts.length} products for primary concern: ${concernKey}`, sliderData[concernKey]);
        }
      }
      
      window.productSlider.renderProductSliders(sliderData);
      
      // Initialize Ryviu widgets after DOM is ready
      setTimeout(() => {
        if (window.Ryviu && window.Ryviu.initWidget) {
          console.log('Initializing Ryviu widgets...');
          window.Ryviu.initWidget();
        } else {
          console.log('Ryviu not found - ratings will not display');
        }
      }, 1000);
    }
  }

  createProductCardHTML(product) {
    // Use the same card structure as createProductCard
    return this.createProductCard(product);
  }

  createRoutineStep(stepInfo, product, index) {
    const stepDiv = document.createElement('div');
    stepDiv.className = 'routine-step';

    const stepNumber = stepInfo.key === 'treatment' && index > 0 
      ? `${stepInfo.step}.${index + 1}` 
      : stepInfo.step;

    stepDiv.innerHTML = `
      <div class="step-header">
        <div class="step-number">${stepNumber}</div>
        <div class="step-title">${stepInfo.title}</div>
      </div>
      <div class="products-grid">
        ${this.createProductCard(product)}
      </div>
    `;

    return stepDiv;
  }

  createRoutineStepPlaceholder(stepInfo) {
    const stepDiv = document.createElement('div');
    stepDiv.className = 'routine-step';
    stepDiv.innerHTML = `
      <div class="step-header">
        <div class="step-number">${stepInfo.step}</div>
        <div class="step-title">${stepInfo.title}</div>
      </div>
      <p style="text-align: center; color: var(--text-medium); padding: 2rem;">
        No product recommended for this step.
      </p>
    `;
    return stepDiv;
  }

  createProductCard(product) {
    // Debug logging
    console.log('Creating card for product:', product.title, {
      vendor: product.vendor,
      tags: product.tags,
      available: product.available,
      hasVariants: product.variants ? product.variants.length : 0,
      images: product.images ? product.images.length : 0,
      firstImageSrc: product.images && product.images[0] ? product.images[0].src : 'NO IMAGE'
    });
    
    // Extract variant data (matches Liquid: product.first_available_variant)
    const variant = product.variants && product.variants[0];
    const price = variant ? variant.price : 0;
    const compareAtPrice = variant && variant.compare_at_price ? variant.compare_at_price : null;
    
    // Calculate on_sale (matches Liquid logic)
    const onSale = compareAtPrice && compareAtPrice > price;
    
    // Check for custom labels in tags (matches Liquid logic)
    let hasCustomLabel = false;
    let customLabel = '';
    if (product.tags) {
      const tagsArray = Array.isArray(product.tags) ? product.tags : product.tags.split(',').map(t => t.trim());
      for (const tag of tagsArray) {
        if (tag.includes('_label_') && tag.startsWith('_')) {
          hasCustomLabel = true;
          customLabel = tag.replace('_label_', '');
          break;
        }
      }
    }
    
    // Get preview image - use original URL from Shopify
    const previewImage = product.images && product.images[0] ? product.images[0] : null;
    const imgUrl = previewImage ? previewImage.src : '';
    
    console.log('Image URL for', product.title, ':', imgUrl);
    
    // Format prices using Shopify money format (matches Liquid | money filter)
    const priceFormatted = this.formatMoney(price);
    const compareAtPriceFormatted = compareAtPrice ? this.formatMoney(compareAtPrice) : null;
    
    // Calculate savings amount (₹) instead of percentage
    let savedAmount = '';
    if (onSale && compareAtPrice) {
      const savedInCents = compareAtPrice - price; // Both are in cents
      savedAmount = this.formatMoney(savedInCents);
    }
    
    // Check availability
    const isAvailable = product.available && (!variant || variant.available);
    
    // Build the EXACT HTML structure from Liquid template
    return `
      <div class="byb-card"
        data-product-id="${product.id}"
        data-variant-id="${variant ? variant.id : ''}"
        data-product-title="${this.escapeHtml(product.title)}"
        data-product-image="${product.images && product.images[0] ? product.images[0].src : ''}"
        data-mrp="${compareAtPrice ? (compareAtPrice / 100) : (price / 100)}">
        
        <div class="grid-product__content">
          
          ${hasCustomLabel ? `
            <div class="grid-product__tag grid-product__tag--custom">
              ${customLabel}
            </div>
          ` : (!isAvailable ? `
            <div class="grid-product__tag grid-product__tag--sold-out">
              Sold Out
            </div>
          ` : (onSale ? `
            <div class="grid-product__tag grid-product__tag--sale">
              Sale
            </div>
          ` : ''))}
          
          <!-- IMAGE CLICKABLE ONLY -->
          <a href="/products/${product.handle}" class="grid-product__link">
            <div class="grid-product__image-mask">
              
              <div class="grid__image-ratio grid__image-ratio--square">
                <img src="${imgUrl || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}"
                     alt="${this.escapeHtml(product.title)}"
                     loading="lazy"
                     onerror="this.onerror=null; this.src='https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';">
              </div>
              
            </div>
          </a>
          
          <!-- META SECTION OUTSIDE THE <a> -->
          <div class="grid-product__meta">
            
            <div class="grid-product__title">
              ${product.title}
            </div>
            
            ${product.vendor ? `<div class="grid-product__vendor">${product.vendor}</div>` : ''}
            
            <!-- ⭐ RYVIU STAR RATING (Hidden if no reviews) -->
            <div class="ryviu-collection" style="min-height: 0;">
              <ryviu-widget-total
                collection="1"
                product_id="${product.id}"
                handle="${product.handle}">
              </ryviu-widget-total>
            </div>
            
            <!-- PRICE BLOCK -->
            <div class="grid-product__price">
              
              ${onSale ? `
                <span class="grid-product__price--original">
                  ${compareAtPriceFormatted}
                </span>
              ` : ''}
              
              <span class="grid-product__price--current">
                ${priceFormatted}
              </span>
              
              ${onSale && savedAmount ? `
                <span class="grid-product__price--savings">
                  Save ${savedAmount}
                </span>
              ` : ''}
              
            </div>
            
            ${variant && variant.unit_price_measurement ? `
              <div class="product__unit-price">
                ${this.formatMoney(variant.unit_price)}/${variant.unit_price_measurement.reference_value !== 1 ? variant.unit_price_measurement.reference_value : ''}${variant.unit_price_measurement.reference_unit}
              </div>
            ` : ''}
            
            <!-- 🎯 THEME BUTTON STYLE — BYB LOGIC -->
            <button
              type="button"
              class="btn btn--full btn--secondary byb-toggle-btn"
              data-variant-id="${variant ? variant.id : ''}"
              ${!isAvailable ? 'disabled' : ''}
              ${!isAvailable ? 'style="cursor: not-allowed; opacity: 0.6;"' : ''}>
              ${isAvailable ? 'Add To Box' : 'Sold Out'}
            </button>
            
          </div>
          
        </div>
      </div>
    `;
  }

  stripHtml(html) {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return String(text).replace(/[&<>"']/g, m => map[m]);
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') cents = 0;
    const amount = (cents / 100).toFixed(2);
    return `₹${amount}`;
  }

  formatConcern(concern) {
    return concern.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  /**
   * Open theme cart drawer if available. Returns true if drawer opened.
   */
  openCartDrawer() {
    // 1) Theme cart drawer (most common)
    if (typeof theme !== 'undefined' && theme.CartDrawer) {
      try {
        if (!this.cartDrawerInstance) {
          this.cartDrawerInstance = new theme.CartDrawer();
          if (typeof this.cartDrawerInstance.init === 'function') {
            this.cartDrawerInstance.init();
          }
        }
        if (typeof this.cartDrawerInstance.open === 'function') {
          this.cartDrawerInstance.open();
          return true;
        }
      } catch (error) {
        console.warn('Cart drawer open failed (theme.CartDrawer):', error);
      }
    }

    // 2) CartUtils helper (if the theme exposes it)
    if (typeof CartUtils !== 'undefined' && typeof CartUtils.openDrawer === 'function') {
      try {
        CartUtils.openDrawer();
        return true;
      } catch (error) {
        console.warn('Cart drawer open failed (CartUtils.openDrawer):', error);
      }
    }

    // 3) Generic event-based drawer open
    try {
      const event = new CustomEvent('cart:open', { bubbles: true });
      document.dispatchEvent(event);
      return true;
    } catch (error) {
      console.warn('Cart drawer open failed (cart:open event):', error);
    }

    return false; // Fallback handled by caller
  }

  setupCartIntegration() {
    // Bulk buttons now handled inside product slider (bundle logic + buy now)

    // Setup "Add to Box" buttons using event delegation (for dynamically created buttons)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.byb-toggle-btn');
      if (btn) {
        // Check if button is disabled (out of stock)
        if (btn.disabled || btn.textContent.trim() === 'Sold Out') {
          e.preventDefault();
          e.stopPropagation();
          alert('This product is currently out of stock.');
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        
        const variantId = btn.dataset.variantId;
        if (!variantId) {
          console.error('No variant ID found on button');
          return;
        }

        // Try theme's BYB functionality first (if available)
        if (typeof window.byb !== 'undefined' && window.byb.toggle) {
          try {
            window.byb.toggle(parseInt(variantId));
            return;
          } catch (error) {
            console.warn('BYB toggle failed, falling back to cart:', error);
          }
        }

        // Fallback to regular cart
        this.addToCart(null, parseInt(variantId));
      }
    });
  }

  async addToCart(productId, variantId) {
    if (!variantId) {
      alert('Product variant not available.');
      return;
    }

    // Use CartUtils if available, otherwise use direct fetch
    if (typeof CartUtils !== 'undefined') {
      const result = await CartUtils.addItem(variantId, 1);
      if (result.success) {
        this.showCartNotification('Product added to cart!');
      } else {
        alert('Failed to add product to cart. Please try again.');
      }
      return;
    }

    // Fallback to direct fetch
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.updateCartCount();
        this.showCartNotification('Product added to cart!');
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      alert('Failed to add product to cart. Please try again.');
    }
  }

  async addAllToCart() {
    const items = [];
    const outOfStockItems = [];
    
    // Collect all product variants from byb-card elements (only available products)
    document.querySelectorAll('.byb-card').forEach(card => {
      const variantId = card.dataset.variantId;
      if (variantId) {
        // Check if product is out of stock by looking at the button
        const button = card.querySelector('.byb-toggle-btn');
        const isOutOfStock = button && (button.disabled || button.textContent.trim() === 'Sold Out');
        
        if (isOutOfStock) {
          const productTitle = card.dataset.productTitle || 'Product';
          outOfStockItems.push(productTitle);
        } else {
          items.push({
            id: parseInt(variantId),
            quantity: 1
          });
        }
      }
    });

    if (items.length === 0) {
      if (outOfStockItems.length > 0) {
        alert('All products in this routine are currently out of stock.');
      } else {
        alert('No products to add.');
      }
      return;
    }

    // Build success message
    let successMessage = `Added ${items.length} item${items.length > 1 ? 's' : ''} to cart!`;
    if (outOfStockItems.length > 0) {
      successMessage += `\n\nNote: ${outOfStockItems.length} product${outOfStockItems.length > 1 ? 's are' : ' is'} out of stock and was${outOfStockItems.length > 1 ? '' : ''} skipped.`;
    }

    // Use CartUtils if available, otherwise use direct fetch
    if (typeof CartUtils !== 'undefined') {
      const result = await CartUtils.addItems(items);
      if (result.success) {
        this.showCartNotification(successMessage);
      } else {
        alert('Failed to add products to cart. Please try again.');
      }
      return;
    }

    // Fallback to direct fetch
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        this.updateCartCount();
        this.showCartNotification(successMessage);
      } else {
        throw new Error('Failed to add items to cart');
      }
    } catch (error) {
      console.error('Add all to cart error:', error);
      alert('Failed to add products to cart. Please try again.');
    }
  }

  async updateCartCount() {
    // Use CartUtils if available
    if (typeof CartUtils !== 'undefined') {
      await CartUtils.updateCartCount();
      return;
    }

    // Fallback to direct fetch
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        // Update cart icon count if element exists
        const cartCountEl = document.getElementById('cart-count');
        if (cartCountEl) {
          cartCountEl.textContent = cart.item_count || 0;
        }
      }
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  showCartNotification(message) {
    // Create notification element
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--primary-pink);
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  // NEW: Circular Gauge Animation
  updateCircularGauge(result) {
    const overallScore = result.skinScore || this.calculateOverallScore(result.concerns || {}, this.analysisData.questionnaire);
    const gaugeArc = document.getElementById('gauge-arc');
    
    if (gaugeArc) {
      // Calculate stroke-dashoffset (534 is full circle circumference)
      const circumference = 534;
      const offset = circumference - (overallScore / 100) * circumference;
      
      setTimeout(() => {
        gaugeArc.style.strokeDashoffset = offset;
      }, 300);
    }
  }

  // NEW: Update Overall Score Badge
  updateOverallScore(result) {
    const overallScore = result.skinScore || this.calculateOverallScore(result.concerns || {}, this.analysisData.questionnaire);
    const scoreDisplay = document.getElementById('overall-score-display');
    const scoreRating = document.getElementById('score-rating');
    
    if (scoreDisplay) {
      scoreDisplay.textContent = Math.round(overallScore);
    }
    
    if (scoreRating) {
      let rating = 'Average';
      let ratingClass = 'average';
      
      if (overallScore >= 85) {
        rating = 'Excellent';
        ratingClass = 'excellent';
      } else if (overallScore >= 70) {
        rating = 'Good';
        ratingClass = 'good';
      } else if (overallScore < 50) {
        rating = 'Needs Attention';
        ratingClass = 'poor';
      }
      
      scoreRating.textContent = rating;
      scoreRating.className = `score-rating ${ratingClass}`;
    }
  }

  // NEW: Update Primary Concern with Root Causes
  updatePrimaryConcern(result) {
    const primaryConcernName = document.getElementById('primary-concern-name');
    const rootCausesList = document.getElementById('root-causes-list');
    
    if (primaryConcernName && result.primaryConcern) {
      primaryConcernName.textContent = this.formatConcern(result.primaryConcern);
    }
    
    if (rootCausesList) {
      const rootCauses = result.metrics?.root_causes || [
        'Excess sebum production clogs pores',
        'Bacterial growth inflames skin',
        'Hormonal imbalances trigger oil glands'
      ];
      
      rootCausesList.innerHTML = rootCauses.map(cause => 
        `<li>${cause}</li>`
      ).join('');
    }
  }

  // NEW: Update Social Proof Comparison
  updateSocialProof(result) {
    const socialText = document.getElementById('social-comparison-text');
    if (socialText) {
      const overallScore = result.skinScore || this.calculateOverallScore(result.concerns || {}, this.analysisData.questionnaire);
      
      // Calculate percentile (simple formula)
      const percentile = Math.min(95, Math.max(5, Math.round(overallScore * 0.9)));
      
      socialText.textContent = `Your skin score is better than ${percentile}% of all face scans today`;
    }
  }

  // NEW: Render primary concerns badges (AI detected only)
  renderPrimaryConcernsBadges(result) {
    const primaryConcerns = document.getElementById('primary-concerns');
    if (!primaryConcerns) return;
    
    // Get AI detected concerns ONLY
    const aiPrimaryConcern = result.primaryConcern;
    const aiSecondaryConcern = result.secondaryConcern;

    console.log('AI detected concerns:', aiPrimaryConcern, aiSecondaryConcern);

    // Show ONLY AI-detected concerns
    const aiConcerns = [];
    
    if (aiPrimaryConcern) {
      aiConcerns.push({
        name: this.formatConcern(aiPrimaryConcern),
        key: aiPrimaryConcern,
        type: 'ai-primary'
      });
    }
    if (aiSecondaryConcern) {
      aiConcerns.push({
        name: this.formatConcern(aiSecondaryConcern),
        key: aiSecondaryConcern,
        type: 'ai-secondary'
      });
    }

    primaryConcerns.innerHTML = aiConcerns
      .map(concern => {
        if (concern.type === 'ai-primary') {
          return `<span class="concern-badge ai-detected primary">🎯 ${concern.name}</span>`;
        } else {
          return `<span class="concern-badge ai-detected secondary">${concern.name}</span>`;
        }
      })
      .join('');
  }

  // NEW: Add carousel dots for score boxes
  addScoreCarouselDots(totalBoxes) {
    const dotsContainer = document.getElementById('score-carousel-dots');
    if (!dotsContainer) return;
    
    // Calculate number of pages (3 boxes per page on mobile, 5 on desktop)
    const isMobile = window.innerWidth <= 768;
    const boxesPerPage = isMobile ? 3 : 5;
    const totalPages = Math.ceil(totalBoxes / boxesPerPage);
    
    if (totalPages <= 1) {
      dotsContainer.innerHTML = '';
      return;
    }
    
    dotsContainer.innerHTML = Array.from({ length: totalPages }, (_, i) => 
      `<div class="carousel-dot ${i === 0 ? 'active' : ''}" data-index="${i}"></div>`
    ).join('');
    
    // Add scroll listener to update active dot
    const scoreBoxes = document.getElementById('score-boxes');
    if (scoreBoxes) {
      scoreBoxes.addEventListener('scroll', () => {
        const scrollLeft = scoreBoxes.scrollLeft;
        const boxWidth = scoreBoxes.scrollWidth / totalPages;
        const activeIndex = Math.round(scrollLeft / boxWidth);
        
        document.querySelectorAll('#score-carousel-dots .carousel-dot').forEach((dot, i) => {
          dot.classList.toggle('active', i === activeIndex);
        });
      });
    }
    
    // Update dots on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.addScoreCarouselDots(totalBoxes);
      }, 250);
    });
  }
}

// Initialize results page
let resultsPage;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    resultsPage = new ResultsPage();
  });
} else {
  resultsPage = new ResultsPage();
}

